import { useEffect, useRef } from 'react'
import { useBlocker } from 'react-router-dom'
import { shouldBlockNavigation } from '../utils/shouldBlockNavigation'

// Max time to wait for the leave-time save before navigating anyway. A flush
// that stalls (e.g. a slow upload) must never trap the user on the board — the
// snapshot is already staged in the store by then, so the save still completes
// in the background.
const LEAVE_FLUSH_TIMEOUT_MS = 5000

interface UseBoardLeaveGuardOptions {
  /** Only guard while actually on a board (e.g. false in embed mode). */
  enabled: boolean
  /** Read the freshest "there are unsaved canvas changes" signal. */
  getHasUnsavedChanges: () => boolean
  /** Capture + persist the current board state. Awaited before navigating. */
  flush: () => Promise<void>
}

/**
 * Save-on-leave guard for the board canvas. Intercepts any in-app navigation
 * away from the board and flushes the pending autosave first, so a stroke drawn
 * within the autosave debounce window isn't lost. Built on React Router's
 * useBlocker, so a single guard covers every exit path (logo, sidebar, back
 * button, board-to-board) instead of wrapping each navigation call site.
 */
export function useBoardLeaveGuard({
  enabled,
  getHasUnsavedChanges,
  flush,
}: UseBoardLeaveGuardOptions): void {
  // Read callbacks through refs so the blocker predicate and the flush effect
  // always see the latest values, never a stale render closure.
  const getHasUnsavedChangesRef = useRef(getHasUnsavedChanges)
  getHasUnsavedChangesRef.current = getHasUnsavedChanges
  const flushRef = useRef(flush)
  flushRef.current = flush

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      enabled &&
      shouldBlockNavigation(
        currentLocation.pathname,
        nextLocation.pathname,
        getHasUnsavedChangesRef.current(),
      ),
  )

  // Latch so we proceed exactly once per blocked navigation (guards against
  // StrictMode double-invocation and effect re-runs while still blocked).
  const proceedingRef = useRef(false)

  useEffect(() => {
    if (blocker.state !== 'blocked') {
      if (blocker.state === 'unblocked') proceedingRef.current = false
      return
    }
    if (proceedingRef.current) return
    proceedingRef.current = true

    let settled = false
    const proceed = () => {
      if (settled) return
      settled = true
      blocker.proceed?.()
    }

    // Proceed when the save resolves or the timeout fires, whichever comes
    // first — a hung save can't trap navigation.
    const timer = setTimeout(proceed, LEAVE_FLUSH_TIMEOUT_MS)
    void (async () => {
      try {
        await flushRef.current()
      } catch (error) {
        console.error('[BoardCanvas] Save-on-leave flush failed:', error)
      } finally {
        clearTimeout(timer)
        proceed()
      }
    })()
  }, [blocker])
}
